/** Shared actor movement: the foot-box collision probe used by the player, NPCs, and animals. */

/** Half-width and the vertical span of the collision box, relative to the feet anchor. */
export const FOOT_BOX = { halfWidth: 0.22, top: 0.08, bottom: 0.38 };

function cornersAt(x, y) {
  const { halfWidth, top, bottom } = FOOT_BOX;
  return [
    [x - halfWidth, y + top], [x + halfWidth, y + top],
    [x - halfWidth, y + bottom], [x + halfWidth, y + bottom],
  ];
}

function cellBlocked(world, fromX, fromY, toX, toY) {
  return world.isMovementBlocked
    ? world.isMovementBlocked(fromX, fromY, toX, toY)
    : world.isCollidable(toX, toY);
}

/** True when any corner of the foot box at `(x, y)` sits on a blocked cell. */
export function isFootBlocked(world, x, y) {
  if (!world?.isCollidable) return false;
  return cornersAt(x, y).some(([px, py]) => world.isCollidable(Math.floor(px), Math.floor(py)));
}

/**
 * True when a straight walk from `(fromX, fromY)` to `(toX, toY)` crosses a
 * cell boundary movement would refuse — a collidable cell, or an elevation
 * step with no ramp. A cheap sampled probe for wander target picking, not a
 * pathfinder: it rejects most doomed targets so actors stop marching into
 * cliff edges and re-idling, but a clear verdict is no guarantee.
 */
export function isPathBlocked(world, fromX, fromY, toX, toY) {
  if (!world?.isCollidable) return false;
  const distance = Math.hypot(toX - fromX, toY - fromY);
  const steps = Math.max(1, Math.ceil(distance * 2));
  let cellX = Math.floor(fromX);
  let cellY = Math.floor(fromY);
  for (let i = 1; i <= steps; i++) {
    const nextX = Math.floor(fromX + ((toX - fromX) * i) / steps);
    const nextY = Math.floor(fromY + ((toY - fromY) * i) / steps);
    if (nextX === cellX && nextY === cellY) continue;
    if (cellBlocked(world, cellX, cellY, nextX, nextY)) return true;
    cellX = nextX;
    cellY = nextY;
  }
  return false;
}

/**
 * Move `actor` along one axis if its foot box stays clear, sliding along walls
 * because each axis resolves independently. Returns whether the actor moved.
 */
export function moveAxis(actor, world, axis, amount) {
  if (!amount) return false;
  const nextX = axis === 'x' ? actor.x + amount : actor.x;
  const nextY = axis === 'y' ? actor.y + amount : actor.y;
  const points = cornersAt(nextX, nextY);
  const previousPoints = cornersAt(actor.x, actor.y);
  const blocked = points.some(([x, y], index) => {
    const [previousX, previousY] = previousPoints[index];
    const fromX = Math.floor(previousX); const fromY = Math.floor(previousY);
    const toX = Math.floor(x); const toY = Math.floor(y);
    return cellBlocked(world, fromX, fromY, toX, toY);
  });
  if (blocked) return false;
  actor[axis] += amount;
  return true;
}
